import { useEffect, useState } from "react";

import { Button, Callout, Icon, Intent } from "@blueprintjs/core";
import { addYears, isBefore } from "date-fns";

import { Box } from "@/components/layout/flexbox";
import { DateTimeInputV2 } from "@/components/pieces/DateTimeInputV2";
import { StyledDialog } from "@/components/pieces/StyledDialog";
import { SUBTEXT_COLOR } from "@/css/constants";
import {
  ProgramComponentsQueryDocument,
  ProgramQueryDocument,
  useLaunchProgramMutation,
  useUpdateProgramMutation,
} from "@/graphql";
import { MainProgram } from "@/types/programs";
import { areDifferentTimes, getBrowserTimezone } from "@/utils/datetime";

import { getMinDate } from "./utils";

let updateTimeoutRef: NodeJS.Timeout;

interface LaunchProgramComponentDialogProps {
  program: MainProgram;
  open: boolean;
  onClose: () => void;
  onSave?: () => void;
  isUpdateNow?: boolean;
}

export function LaunchProgramComponentDialog({
  program,
  open,
  onClose,
  onSave = () => {},
  isUpdateNow = false,
}: LaunchProgramComponentDialogProps) {
  const [launchDate, setLaunchDate] = useState<string | null>();
  const [errorMessage, setErrorMessage] = useState<string>();
  const [timezone, setTimezone] = useState(getBrowserTimezone());
  const [loading, setLoading] = useState(false);
  const isNewData = launchDate
    ? areDifferentTimes(launchDate, program?.targetLaunchDate)
    : false;

  const minDate = getMinDate([launchDate, new Date()], isNewData) || new Date();
  const maxDate = addYears(new Date(), 1);

  const onCompleted = () => {
    onSave();
    onClose();
    setLoading(false);
  };

  const onError = (err: Error) => {
    setErrorMessage(err.message);
    setLoading(false);
  };

  const refetchQueries = [
    {
      query: ProgramQueryDocument,
      variables: { programId: program?.id },
    },
    {
      query: ProgramComponentsQueryDocument,
      variables: { programId: program?.id },
    },
  ];

  const [updateProgram, { loading: updating }] = useUpdateProgramMutation({
    onCompleted: () => {
      onCompleted();
    },
    onError,
    refetchQueries,
  });

  const [launchProgram, { loading: launching }] = useLaunchProgramMutation({
    onCompleted: (data) => {
      if (data?.launchProgram?.ok) {
        onCompleted();
      } else {
        onError(Error("Something went wrong. Please try again."));
      }
    },
    onError,
    refetchQueries,
  });

  useEffect(() => {
    return () => clearTimeout(updateTimeoutRef);
  }, []);

  useEffect(() => {
    if (program) {
      const { targetLaunchDate } = program;
      setLaunchDate(targetLaunchDate ?? undefined);
    }
  }, [program]);

  const onUpdateProgram = () => {
    setLoading(true);

    clearTimeout(updateTimeoutRef);
    updateTimeoutRef = setTimeout(() => {
      if (!isUpdateNow && !launchDate) {
        onError(Error("Please select a launch date"));
        return;
      }
      if (
        launchDate &&
        isBefore(new Date(launchDate), new Date()) &&
        !isUpdateNow
      ) {
        onError(Error("Launch date cannot be in the past"));
        return;
      }
      if (isUpdateNow) {
        launchProgram({ variables: { programId: program.id } });
      } else {
        const launchDateData = isUpdateNow
          ? new Date().toISOString()
          : launchDate;

        // Avoid changing the program name
        updateProgram({
          variables: {
            name: null,
            programId: program?.id,
            targetLaunchDate: launchDateData,
            scheduleLaunchEmail: null,
          },
        });
      }
    }, 500);
  };

  return (
    <StyledDialog
      isOpen={open}
      lazy={true}
      title="Are you sure you want to launch now?"
      width="480px"
      onClose={() => {
        onClose();
      }}
      onOpening={() => {
        setErrorMessage(undefined);
      }}
    >
      <Box p={24}>
        {errorMessage && (
          <Box mb={3}>
            <Callout icon={null} intent={Intent.DANGER}>
              {errorMessage}
            </Callout>
          </Box>
        )}
        {isUpdateNow ? (
          <Box>
            All of your participants will be notified. This is not reversible.
          </Box>
        ) : (
          <>
            <Box fontWeight={600} fontSize={16}>
              Launch Date
            </Box>
            <Box fontSize={14} color={SUBTEXT_COLOR} mb={2}>
              The date and time your participants will receive invitations
            </Box>
          </>
        )}
        {!isUpdateNow && (
          <Box css={{ position: "relative" }}>
            <Icon
              icon="calendar"
              style={{
                position: "absolute",
                top: 12,
                left: 10,
                zIndex: 99,
                color: SUBTEXT_COLOR,
              }}
            />
            <DateTimeInputV2
              onChange={setLaunchDate}
              value={launchDate}
              timezone={timezone}
              onChangeTimezone={setTimezone}
              maxDateTime={maxDate}
              minDateTime={minDate}
            />
          </Box>
        )}
        <Box mt={24}>
          <Button
            intent="primary"
            large={true}
            disabled={updating || launching || loading}
            onClick={onUpdateProgram}
            loading={updating || launching || loading}
          >
            {isUpdateNow ? "Launch Now" : "Save"}
          </Button>
          {isUpdateNow && (
            <Button
              style={{ marginLeft: 10 }}
              intent="none"
              large
              onClick={onClose}
            >
              Nevermind
            </Button>
          )}
        </Box>
      </Box>
    </StyledDialog>
  );
}
